import React, { useEffect, useState } from 'react';
import { reportApi } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';

// ─── Hilfsfunktionen ────────────────────────────────────────────────────────

function max(arr, key) {
  return arr.reduce((m, x) => Math.max(m, x[key]), 0) || 1;
}

function shortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function shortSender(sender) {
  // Extrahiert Anzeigename oder verkürzt lange Adressen
  const m = sender.match(/^"?([^"<]+)"?\s*</);
  if (m) return m[1].trim();
  if (sender.length > 40) return sender.slice(0, 40) + '…';
  return sender;
}

// ─── Teilkomponenten ─────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color = 'blue' }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    green: 'bg-green-50 text-green-700 border-green-100',
    purple: 'bg-purple-50 text-purple-700 border-purple-100',
  };
  return (
    <div className={`rounded-xl border p-5 ${colors[color]}`}>
      <div className="text-xs font-medium uppercase tracking-wide opacity-70 mb-1">{label}</div>
      <div className="text-3xl font-bold">{value}</div>
      {sub && <div className="text-xs mt-1 opacity-60">{sub}</div>}
    </div>
  );
}

function BarChart({ data, keyX, keyY, label, height = 100 }) {
  if (!data.length) return <div className="text-sm text-gray-400 py-6 text-center">Keine Daten</div>;
  const peak = max(data, keyY);
  return (
    <div>
      <div className="flex items-end gap-1" style={{ height }}>
        {data.map((d, i) => {
          const pct = Math.round((d[keyY] / peak) * 100);
          return (
            <div key={i} className="flex-1 flex flex-col items-center justify-end group relative">
              {/* Tooltip */}
              <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                {d[keyX]}: {d[keyY]}
              </div>
              <div
                className="w-full bg-blue-500 hover:bg-blue-600 rounded-t transition-colors"
                style={{ height: `${Math.max(pct, 2)}%` }}
              />
            </div>
          );
        })}
      </div>
      {/* X-Achse: nur jeden 5. Tag beschriften */}
      <div className="flex gap-1 mt-1">
        {data.map((d, i) => (
          <div key={i} className="flex-1 text-center text-xs text-gray-400 overflow-hidden">
            {i % 5 === 0 ? shortDate(d[keyX]) : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

function HBar({ name, count, total, color = 'bg-blue-500' }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className="w-32 text-sm text-gray-700 truncate flex-shrink-0" title={name}>{name}</div>
      <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="w-14 text-right text-xs text-gray-500">{count.toLocaleString('de-DE')}</div>
      <div className="w-10 text-right text-xs text-gray-400">{pct}%</div>
    </div>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

const CAT_COLORS = [
  'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-yellow-500',
  'bg-red-500', 'bg-indigo-500', 'bg-pink-500', 'bg-teal-500',
];

export default function PortalReports() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    reportApi.stats()
      .then(({ data }) => setStats(data))
      .catch(() => setError('Statistiken konnten nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6"><LoadingSpinner /></div>;

  if (error) return (
    <div className="p-6 text-red-600 bg-red-50 rounded-xl border border-red-200">{error}</div>
  );

  const totalMails = stats.categories.reduce((s, c) => s + c.count, 0);
  const totalVolume = stats.volume.reduce((s, v) => s + v.count, 0);
  const topCat = stats.categories[0]?.name || '–';
  const topSender = stats.top_senders[0] ? shortSender(stats.top_senders[0].sender) : '–';

  return (
    <div className="p-6 space-y-8">
      <h1 className="text-2xl font-bold text-gray-900">Berichte</h1>

      {/* Summary-Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="E-Mails gesamt" value={totalMails.toLocaleString('de-DE')} sub="alle Zeiträume" color="blue" />
        <StatCard label="Letzten 30 Tage" value={totalVolume.toLocaleString('de-DE')} sub="eingegangene Mails" color="green" />
        <StatCard label="Häufigste Kategorie" value={topCat} sub={`Top-Absender: ${topSender}`} color="purple" />
      </div>

      {/* Mailvolumen */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <h2 className="text-base font-semibold text-gray-800 mb-4">
          Mailvolumen — letzte 30 Tage
          <span className="ml-2 text-sm font-normal text-gray-400">({totalVolume} Mails)</span>
        </h2>
        {stats.volume.length === 0
          ? <div className="text-sm text-gray-400 py-6 text-center">Keine Mails in den letzten 30 Tagen</div>
          : <BarChart data={stats.volume} keyX="day" keyY="count" height={140} />
        }
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Kategorienverteilung */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Kategorienverteilung</h2>
          {stats.categories.length === 0
            ? <div className="text-sm text-gray-400 py-6 text-center">Keine Daten</div>
            : (
              <div className="space-y-0.5">
                {stats.categories.map((c, i) => (
                  <HBar
                    key={c.name}
                    name={c.name}
                    count={c.count}
                    total={totalMails}
                    color={CAT_COLORS[i % CAT_COLORS.length]}
                  />
                ))}
              </div>
            )
          }
        </div>

        {/* Top-Absender */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Top 10 Absender</h2>
          {stats.top_senders.length === 0
            ? <div className="text-sm text-gray-400 py-6 text-center">Keine Daten</div>
            : (
              <div className="space-y-0.5">
                {stats.top_senders.map((s, i) => (
                  <HBar
                    key={s.sender}
                    name={shortSender(s.sender)}
                    count={s.count}
                    total={stats.top_senders[0].count}
                    color="bg-indigo-500"
                  />
                ))}
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
}
